import type { FC } from 'react';
import { useContext, useState } from 'react';
import classNames from 'classnames';
import { useFetch } from 'loti-request';
import { useTranslation } from 'react-i18next';

import ConfigContext from 'app/common/ConfigContext';
import { getGameFetchUrl } from 'app/utils/entities/match';
import { sort } from 'app/utils/array';
import CircleLoader from 'app/components/CircleLoader/CircleLoader';
import LoadingContainer from 'app/components/LoadingContainer';
import MapNameTag from 'app/components/MapNameTag';
import TeamName from 'app/components/TeamName';
import Tooltip from 'app/components/Tooltip';
import { CS_VIDEOGAME_TITLES } from 'app/config/videogameSpecific.config';
import VideogameVersionTag from 'app/components/VideogameVersionTag';
import { ERROR_UNKNOWN } from 'app/constants/errors';

import RoundsRecap from './RoundsRecap';
import style from './style.module.scss';
import Dota2MatchRecap from './Dota2MatchRecap';
import { getMapRecapText } from './utils';
import LolMatchRecap from './LolMatchRecap';

interface Props {
  encounter: Encounter;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
}

const MatchesRecap: FC<Props> = ({ encounter, opponent1, opponent2 }) => {
  const { t } = useTranslation();

  const configContext = useContext(ConfigContext);

  const sortedGames = sort(encounter.games, game => game.position);

  const [selectedGame, setSelectedGame] = useState<GameReference>(
    sortedGames.filter(game => game.complete)[0]
  );

  const {
    status,
    data: displayedGame,
    error
  } = useFetch<DetailedGame>({
    url: getGameFetchUrl(encounter.videogame_slug, selectedGame.id, configContext)
  });

  return (
    <div className={style.matchesRecap}>
      <h3 className={style.serieName}>
        {encounter.league_name} {encounter.serie_full_name} {encounter.tournament_name}{' '}
        <VideogameVersionTag name={encounter.videogame_title} />
      </h3>
      <div className={style.matchesContainer}>
        <nav className={style.mapsNav}>
          {sortedGames.map(game => {
            const text = t(getMapRecapText(encounter.videogame_slug), {
              gameNumber: game.position
            });

            if (game.forfeit) {
              return (
                <Tooltip
                  content={`${text} ${t('component_match-recap_map-game-forfeited')}`}
                  key={game.id}
                  className={classNames(style.mapButton, style.fixtureOnlyMatch)}
                >
                  {text}
                </Tooltip>
              );
            }
            if (game.complete) {
              return (
                <button
                  type="button"
                  onClick={() => setSelectedGame(game)}
                  key={game.id}
                  className={classNames(style.mapButton, {
                    [style.selected]: selectedGame.id === game.id
                  })}
                >
                  {text}
                </button>
              );
            }
            return (
              <Tooltip
                content={`${t('component_match-recap_map-no-data')} ${text}`}
                key={game.id}
                className={classNames(style.mapButton, style.fixtureOnlyMatch)}
              >
                {text}
              </Tooltip>
            );
          })}
        </nav>
        <TeamName.Container className={style.teamsNamesContainer}>
          <TeamName
            team={opponent1}
            className={style.teamName}
            teamNamesClassName={style.teamNameText}
          />
          {displayedGame && displayedGame.map && <MapNameTag name={displayedGame.map.name} />}
          <TeamName
            team={opponent2}
            className={style.teamName}
            teamNamesClassName={style.teamNameText}
            orientation="reversed"
          />
        </TeamName.Container>
        <LoadingContainer
          data={displayedGame}
          status={status}
          error={error ? ERROR_UNKNOWN : null}
          loader={
            <div className={style.loaderContainer}>
              <CircleLoader size={40} />
            </div>
          }
        >
          {displayedGame &&
            (() => {
              switch (encounter.videogame_slug) {
                case 'cs-go': {
                  const game = displayedGame as GameCs;
                  return (
                    <RoundsRecap
                      rounds={game.rounds}
                      opponents={game.opponents}
                      variation={CS_VIDEOGAME_TITLES[encounter.videogame_title] ?? 'cs-go'}
                      teamIds={[
                        opponent1?.id || game.opponents.at(0)?.id || -1,
                        opponent2?.id || game.opponents.at(1)?.id || -1
                      ]}
                      map={game.map}
                    />
                  );
                }
                case 'dota-2': {
                  return (
                    <Dota2MatchRecap
                      game={displayedGame as DetailedGameDota2}
                      opponent1={opponent1}
                      opponent2={opponent2}
                    />
                  );
                }
                case 'league-of-legends': {
                  return (
                    <LolMatchRecap
                      game={displayedGame as GameLoL}
                      opponent1={opponent1}
                      opponent2={opponent2}
                    />
                  );
                }
                default:
                  return null;
              }
            })()}
        </LoadingContainer>
      </div>
    </div>
  );
};

MatchesRecap.displayName = 'MatchesRecap';

export default MatchesRecap;
